/**
 * Image utility functions for Sanity
 * Based on the old SanityImage.js
 */

import createImageUrlBuilder from '@sanity/image-url'
import { dataset, projectId } from './sanity'

// Validate Sanity configuration
const isSanityConfigured = Boolean(projectId && dataset);

// Print warning if Sanity is not configured
if (!isSanityConfigured) {
  console.warn(
    'Sanity is not properly configured. Make sure NEXT_PUBLIC_SANITY_PROJECT_ID and NEXT_PUBLIC_SANITY_DATASET are set in your .env file.'
  );
}

const imageBuilder = createImageUrlBuilder({
  projectId: projectId || '',
  dataset: dataset || '',
})

/**
 * Generate image URL from Sanity image reference
 * @param {Object|null|undefined} source - Sanity image object
 * @param {Object} options - Additional options
 * @returns {string|null} - Image URL or null if not valid
 */
export const urlForImage = (source, options = {}) => {
  // If Sanity is not configured, use fallback or return null
  if (!isSanityConfigured) {
    console.warn('Trying to use urlForImage but Sanity is not configured');
    return source?.url || null;
  }

  // Handle null/undefined source
  if (!source) {
    console.warn('Undefined or null image source passed to urlForImage');
    return null;
  }

  // Handle string sources (direct URLs)
  if (typeof source === 'string') {
    return source;
  }

  // Handle fallback URL if specified in the source object
  if (source.url) {
    return source.url;
  }

  // Validate Sanity image object - must have asset with _ref or _id
  if (!source?.asset?._ref && !source?.asset?._id) {
    console.warn('Invalid Sanity image object passed to urlForImage:', source);
    return null;
  }

  try {
    // Build image URL with default transformations
    const { width, height, format, quality, blur } = options;
    
    let urlBuilder = imageBuilder.image(source).auto('format').fit('max');
    
    if (width) urlBuilder = urlBuilder.width(width);
    if (height) urlBuilder = urlBuilder.height(height);
    if (format) urlBuilder = urlBuilder.format(format);
    if (quality) urlBuilder = urlBuilder.quality(quality);
    if (blur) urlBuilder = urlBuilder.blur(blur);
    
    return urlBuilder.url();
  } catch (err) {
    console.error('Error generating image URL:', err);
    return null;
  }
}

/**
 * Helper to generate a responsive image URL with width
 */
export const urlForImageWithWidth = (image, width) => {
  return urlForImage(image, { width, auto: 'format' });
}

/**
 * Helper to generate a blurred image placeholder
 */
export const getImageDimensions = (image) => {
  if (!image?.asset?._ref) {
    return { width: 0, height: 0, aspectRatio: 0 }
  }
  
  const dimensions = image.asset._ref.split('-')[2]
  const [width, height] = dimensions.split('x').map(Number)
  const aspectRatio = width / height
  
  return { width, height, aspectRatio }
} 